import { makeAuthenticatedRequest } from './authService';
import { logApiConfig } from '../config/api';

// Log API configuration in development
if (process.env.NODE_ENV === 'development') {
  logApiConfig();
}

// Learning Progress related types
export interface LearningProgress {
  progress_id: string;
  user_id: number;
  module_id: string;
  activity_id?: string;
  progress_type: 'module' | 'activity';
  status: 'not_started' | 'in_progress' | 'completed' | 'paused';
  completion_percentage: number;
  time_spent_minutes: number;
  last_accessed_at?: string;
  started_at?: string;
  completed_at?: string;
  notes?: string;
  score?: number;
  attempts: number;
  metadata?: any;
  created_at: string;
  updated_at: string;
  // Joined fields from learning_module
  module_title?: string;
  module_category?: string;
}

export interface CreateLearningProgressData {
  module_id: string;
  activity_id?: string;
  progress_type: 'module' | 'activity';
  status?: 'not_started' | 'in_progress' | 'completed' | 'paused';
  completion_percentage?: number;
  time_spent_minutes?: number;
  notes?: string;
  score?: number;
  attempts?: number;
  metadata?: any;
}

export interface UpdateLearningProgressData {
  status?: 'not_started' | 'in_progress' | 'completed' | 'paused';
  completion_percentage?: number;
  time_spent_minutes?: number;
  notes?: string;
  score?: number;
  attempts?: number;
  metadata?: any;
}

export interface LearningProgressSearchParams {
  module_id?: string;
  status?: 'not_started' | 'in_progress' | 'completed' | 'paused';
  progress_type?: 'module' | 'activity';
  limit?: number;
  offset?: number;
}

export interface ModuleProgress {
  module_progress: LearningProgress | null;
  activity_progress: LearningProgress[];
  module_info?: {
    module_id: string;
    title: string;
  };
}

export interface ProgressStatistics {
  total_progress_records: number;
  modules_tracked: number;
  activities_tracked: number;
  completed_items: number;
  in_progress_items: number;
  total_time_spent_minutes: number;
  total_time_spent_hours: number;
  avg_completion_percentage: number;
  avg_score: number | null;
}

export interface RecentActivity {
  progress_id: string;
  module_id: string;
  activity_id?: string;
  progress_type: 'module' | 'activity';
  status: string;
  completion_percentage: number;
  last_accessed_at: string;
  module_title: string;
}

export interface UserProgressStats {
  stats: ProgressStatistics;
  recent_activity: RecentActivity[];
}

class LearningProgressService {
  // ================================================
  // LEARNING PROGRESS MANAGEMENT
  // ================================================

  // Create a new learning progress record
  async createLearningProgress(progressData: CreateLearningProgressData): Promise<{ success: boolean; progress_id?: string; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest('/learning-progress', {
        method: 'POST',
        body: JSON.stringify(progressData)
      });
      
      return response;
    } catch (error: any) {
      console.error('Error creating learning progress:', error);
      
      // If it's a 409 conflict (progress already exists), try to find the existing record
      if (error.status === 409 || (error.message && error.message.includes('Progress already exists')) || 
          (error.message && error.message.includes('Existing progress record found'))) {
        console.log('Progress already exists, attempting to find existing record...');
        
        try {
          const existingProgress = await this.getProgressByModuleAndActivity(
            progressData.module_id, 
            progressData.activity_id
          );
          
          if (existingProgress) {
            console.log(`Found existing progress record: ${existingProgress.progress_id}`);
            return {
              success: true,
              progress_id: existingProgress.progress_id,
              message: 'Found existing progress record'
            };
          }
        } catch (fetchError) {
          console.error('Error fetching existing progress:', fetchError);
        }
      }
      
      // If we still don't have a record, check if the error response contains progress_id
      if (error.progress_id) {
        return {
          success: true,
          progress_id: error.progress_id,
          message: 'Retrieved progress_id from error response'
        };
      }
      
      throw error;
    }
  }

  // Update an existing learning progress record
  async updateLearningProgress(progressId: string, updateData: UpdateLearningProgressData): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/learning-progress/${progressId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
      
      return response;
    } catch (error) {
      console.error('Error updating learning progress:', error);
      throw error;
    }
  }

  // Delete a learning progress record
  async deleteLearningProgress(progressId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await makeAuthenticatedRequest(`/learning-progress/${progressId}`, {
        method: 'DELETE'
      });
      
      return response;
    } catch (error) {
      console.error('Error deleting learning progress:', error);
      throw error;
    }
  }

  // Get learning progress by ID
  async getLearningProgressById(progressId: string): Promise<LearningProgress | null> {
    try {
      const response = await makeAuthenticatedRequest(`/learning-progress/${progressId}`, {
        method: 'GET'
      });
      
      if (response.success && response.progress) {
        return response.progress as LearningProgress;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting learning progress by ID:', error);
      return null;
    }
  }

  // Get user's learning progress with filtering
  async getUserLearningProgress(filters?: LearningProgressSearchParams): Promise<{
    progress: LearningProgress[];
    total: number;
    pagination: { limit: number; offset: number };
  }> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (filters?.module_id) queryParams.append('module_id', filters.module_id);
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.progress_type) queryParams.append('progress_type', filters.progress_type);
      if (filters?.limit) queryParams.append('limit', filters.limit.toString());
      if (filters?.offset) queryParams.append('offset', filters.offset.toString());
      
      const queryString = queryParams.toString();
      const url = `/learning-progress/user/progress${queryString ? `?${queryString}` : ''}`;
      
      const response = await makeAuthenticatedRequest(url, {
        method: 'GET'
      });
      
      if (response.success) {
        return {
          progress: response.progress || [],
          total: response.total || 0,
          pagination: response.pagination || { limit: 50, offset: 0 }
        };
      }
      
      return { progress: [], total: 0, pagination: { limit: 50, offset: 0 } };
    } catch (error) {
      console.error('Error getting user learning progress:', error);
      return { progress: [], total: 0, pagination: { limit: 50, offset: 0 } };
    }
  }

  // Get progress for a specific module
  async getModuleProgress(moduleId: string): Promise<ModuleProgress> {
    try {
      const response = await makeAuthenticatedRequest(`/learning-progress/module/${encodeURIComponent(moduleId)}`, {
        method: 'GET'
      });
      
      if (response.success) {
        return {
          module_progress: response.module_progress || null,
          activity_progress: response.activity_progress || [],
          module_info: response.module_info || null
        };
      }
      
      return {
        module_progress: null,
        activity_progress: []
      };
    } catch (error) {
      console.error('Error getting module progress:', error);
      return {
        module_progress: null,
        activity_progress: []
      };
    }
  }

  // Get user's progress statistics
  async getUserProgressStats(): Promise<UserProgressStats> {
    try {
      const response = await makeAuthenticatedRequest('/learning-progress/user/stats', {
        method: 'GET'
      });
      
      if (response.success) {
        return {
          stats: response.stats || {},
          recent_activity: response.recent_activity || []
        };
      }
      
      return {
        stats: {
          total_progress_records: 0,
          modules_tracked: 0,
          activities_tracked: 0,
          completed_items: 0,
          in_progress_items: 0,
          total_time_spent_minutes: 0,
          total_time_spent_hours: 0,
          avg_completion_percentage: 0,
          avg_score: null
        },
        recent_activity: []
      };
    } catch (error) {
      console.error('Error getting user progress stats:', error);
      return {
        stats: {
          total_progress_records: 0,
          modules_tracked: 0,
          activities_tracked: 0,
          completed_items: 0,
          in_progress_items: 0,
          total_time_spent_minutes: 0,
          total_time_spent_hours: 0,
          avg_completion_percentage: 0,
          avg_score: null
        },
        recent_activity: []
      };
    }
  }

  // ================================================
  // PROGRESS TRACKING SHORTCUTS
  // ================================================

  // Start a module (create progress record with 'in_progress' status)
  async startModule(moduleId: string, initialData?: Partial<CreateLearningProgressData>): Promise<{ success: boolean; progress_id?: string; message?: string }> {
    try {
      const progressData: CreateLearningProgressData = {
        module_id: moduleId,
        progress_type: 'module',
        status: 'in_progress',
        completion_percentage: 0,
        time_spent_minutes: 0,
        attempts: 0,
        ...initialData
      };

      return await this.createLearningProgress(progressData);
    } catch (error) {
      console.error('Error starting module:', error);
      throw error;
    }
  }

  // Start an activity
  async startActivity(moduleId: string, activityId: string, initialData?: Partial<CreateLearningProgressData>): Promise<{ success: boolean; progress_id?: string; message?: string }> {
    try {
      const progressData: CreateLearningProgressData = {
        module_id: moduleId,
        activity_id: activityId,
        progress_type: 'activity',
        status: 'in_progress',
        completion_percentage: 0,
        time_spent_minutes: 0,
        attempts: 0,
        ...initialData
      };

      return await this.createLearningProgress(progressData);
    } catch (error) {
      console.error('Error starting activity:', error);
      throw error;
    }
  }

  // Update progress percentage
  async updateProgress(progressId: string, completionPercentage: number, additionalData?: Partial<UpdateLearningProgressData>): Promise<{ success: boolean; message?: string }> {
    try {
      const updateData: UpdateLearningProgressData = {
        completion_percentage: completionPercentage,
        status: completionPercentage >= 100 ? 'completed' : 'in_progress',
        ...additionalData
      };

      return await this.updateLearningProgress(progressId, updateData);
    } catch (error) {
      console.error('Error updating progress:', error);
      throw error;
    }
  }

  // Mark as completed
  async markCompleted(progressId: string, finalData?: Partial<UpdateLearningProgressData>): Promise<{ success: boolean; message?: string }> {
    try {
      const updateData: UpdateLearningProgressData = {
        status: 'completed',
        completion_percentage: 100,
        ...finalData
      };

      return await this.updateLearningProgress(progressId, updateData);
    } catch (error) {
      console.error('Error marking as completed:', error);
      throw error;
    }
  }

  // Pause progress
  async pauseProgress(progressId: string, notes?: string): Promise<{ success: boolean; message?: string }> {
    try {
      const updateData: UpdateLearningProgressData = {
        status: 'paused',
        ...(notes && { notes })
      };

      return await this.updateLearningProgress(progressId, updateData);
    } catch (error) {
      console.error('Error pausing progress:', error);
      throw error;
    }
  }

  // Resume progress
  async resumeProgress(progressId: string): Promise<{ success: boolean; message?: string }> {
    try {
      const updateData: UpdateLearningProgressData = {
        status: 'in_progress'
      };

      return await this.updateLearningProgress(progressId, updateData);
    } catch (error) {
      console.error('Error resuming progress:', error);
      throw error;
    }
  }

  // Add time spent
  async addTimeSpent(progressId: string, additionalMinutes: number): Promise<{ success: boolean; message?: string }> {
    try {
      // First get current progress to add to existing time
      const currentProgress = await this.getLearningProgressById(progressId);
      if (!currentProgress) {
        throw new Error('Progress record not found');
      }

      const newTimeSpent = currentProgress.time_spent_minutes + additionalMinutes;
      
      const updateData: UpdateLearningProgressData = {
        time_spent_minutes: newTimeSpent
      };

      return await this.updateLearningProgress(progressId, updateData);
    } catch (error) {
      console.error('Error adding time spent:', error);
      throw error;
    }
  }

  // Record attempt (increment attempts counter)
  async recordAttempt(progressId: string, score?: number): Promise<{ success: boolean; message?: string }> {
    try {
      // First get current progress to increment attempts
      const currentProgress = await this.getLearningProgressById(progressId);
      if (!currentProgress) {
        throw new Error('Progress record not found');
      }

      const updateData: UpdateLearningProgressData = {
        attempts: currentProgress.attempts + 1,
        ...(score !== undefined && { score })
      };

      return await this.updateLearningProgress(progressId, updateData);
    } catch (error) {
      console.error('Error recording attempt:', error);
      throw error;
    }
  }

  // ================================================
  // UTILITY FUNCTIONS
  // ================================================

  // Get progress by module and activity
  async getProgressByModuleAndActivity(moduleId: string, activityId?: string): Promise<LearningProgress | null> {
    try {
      const filters: LearningProgressSearchParams = {
        module_id: moduleId,
        progress_type: activityId ? 'activity' : 'module',
        limit: 50  // Increase limit to ensure we find the right record
      };

      const result = await this.getUserLearningProgress(filters);
      
      if (result.progress.length > 0) {
        // If looking for activity, filter by activity_id
        if (activityId) {
          const activityProgress = result.progress.find(p => p.activity_id === activityId);
          return activityProgress || null;
        } else {
          // For module-level progress, find the one without activity_id
          const moduleProgress = result.progress.find(p => !p.activity_id && p.progress_type === 'module');
          return moduleProgress || null;
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error getting progress by module and activity:', error);
      return null;
    }
  }

  // Check if progress exists
  async progressExists(moduleId: string, activityId?: string): Promise<boolean> {
    try {
      const progress = await this.getProgressByModuleAndActivity(moduleId, activityId);
      return progress !== null;
    } catch (error) {
      console.error('Error checking if progress exists:', error);
      return false;
    }
  }

  // Get progress by status
  async getProgressByStatus(status: 'not_started' | 'in_progress' | 'completed' | 'paused'): Promise<LearningProgress[]> {
    try {
      const result = await this.getUserLearningProgress({ status });
      return result.progress;
    } catch (error) {
      console.error('Error getting progress by status:', error);
      return [];
    }
  }

  // Get completed progress
  async getCompletedProgress(): Promise<LearningProgress[]> {
    return this.getProgressByStatus('completed');
  }

  // Get in-progress items
  async getInProgressItems(): Promise<LearningProgress[]> {
    return this.getProgressByStatus('in_progress');
  }

  // Get paused items
  async getPausedItems(): Promise<LearningProgress[]> {
    return this.getProgressByStatus('paused');
  }

  // Calculate module completion percentage based on activities
  calculateModuleCompletion(moduleProgress: ModuleProgress): number {
    const { activity_progress } = moduleProgress;
    
    if (activity_progress.length === 0) {
      return moduleProgress.module_progress?.completion_percentage || 0;
    }

    const totalActivities = activity_progress.length;
    const completedActivities = activity_progress.filter(a => a.status === 'completed').length;
    
    return Math.round((completedActivities / totalActivities) * 100);
  }

  // Calculate total time spent across all progress
  calculateTotalTimeSpent(progressList: LearningProgress[]): { minutes: number; hours: number; formatted: string } {
    const totalMinutes = progressList.reduce((sum, progress) => sum + progress.time_spent_minutes, 0);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return {
      minutes: totalMinutes,
      hours: Math.round(totalMinutes / 60 * 100) / 100,
      formatted: `${hours}h ${minutes}m`
    };
  }

  // Get learning streak (consecutive days with activity)
  async getLearningStreak(): Promise<number> {
    try {
      const result = await this.getUserLearningProgress({ limit: 500 });
      const progressWithDates = result.progress
        .filter(p => p.last_accessed_at)
        .map(p => ({
          ...p,
          accessDate: new Date(p.last_accessed_at!).toDateString()
        }))
        .sort((a, b) => new Date(b.last_accessed_at!).getTime() - new Date(a.last_accessed_at!).getTime());

      if (progressWithDates.length === 0) return 0;

      // Get unique dates
      const uniqueDates = [...new Set(progressWithDates.map(p => p.accessDate))];
      
      let streak = 0;
      const today = new Date().toDateString();
      let currentDate = new Date();

      for (const dateStr of uniqueDates) {
        if (dateStr === currentDate.toDateString()) {
          streak++;
          currentDate.setDate(currentDate.getDate() - 1);
        } else {
          break;
        }
      }

      return streak;
    } catch (error) {
      console.error('Error calculating learning streak:', error);
      return 0;
    }
  }

  // ================================================
  // VALIDATION AND FORMATTING
  // ================================================

  // Validate progress data
  validateProgressData(progressData: Partial<CreateLearningProgressData>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!progressData.module_id || progressData.module_id.trim() === '') {
      errors.push('Module ID is required');
    }

    if (!progressData.progress_type || !['module', 'activity'].includes(progressData.progress_type)) {
      errors.push('Progress type must be either "module" or "activity"');
    }

    if (progressData.progress_type === 'activity' && (!progressData.activity_id || progressData.activity_id.trim() === '')) {
      errors.push('Activity ID is required for activity progress');
    }

    if (progressData.status && !['not_started', 'in_progress', 'completed', 'paused'].includes(progressData.status)) {
      errors.push('Status must be one of: not_started, in_progress, completed, paused');
    }

    if (progressData.completion_percentage !== undefined && (progressData.completion_percentage < 0 || progressData.completion_percentage > 100)) {
      errors.push('Completion percentage must be between 0 and 100');
    }

    if (progressData.score !== undefined && (progressData.score < 0 || progressData.score > 100)) {
      errors.push('Score must be between 0 and 100');
    }

    if (progressData.time_spent_minutes !== undefined && progressData.time_spent_minutes < 0) {
      errors.push('Time spent must be non-negative');
    }

    if (progressData.attempts !== undefined && progressData.attempts < 0) {
      errors.push('Attempts must be non-negative');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Format progress for display
  formatProgressForDisplay(progress: LearningProgress): {
    id: string;
    moduleId: string;
    moduleTitle: string;
    activityId: string | null;
    type: string;
    typeLabel: string;
    status: string;
    statusLabel: string;
    completionPercentage: number;
    completionLabel: string;
    timeSpent: string;
    score: string;
    attempts: number;
    lastAccessed: string;
    notes: string;
  } {
    const typeLabels = {
      module: 'Module',
      activity: 'Activity'
    };

    const statusLabels = {
      not_started: 'Not Started',
      in_progress: 'In Progress',
      completed: 'Completed',
      paused: 'Paused'
    };

    const formatTime = (minutes: number): string => {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (hours > 0) {
        return `${hours}h ${mins}m`;
      }
      return `${mins}m`;
    };

    const formatDate = (dateString?: string): string => {
      if (!dateString) return 'Never';
      return new Date(dateString).toLocaleDateString();
    };

    return {
      id: progress.progress_id,
      moduleId: progress.module_id,
      moduleTitle: progress.module_title || 'Unknown Module',
      activityId: progress.activity_id || null,
      type: progress.progress_type,
      typeLabel: typeLabels[progress.progress_type],
      status: progress.status,
      statusLabel: statusLabels[progress.status],
      completionPercentage: progress.completion_percentage,
      completionLabel: `${progress.completion_percentage}%`,
      timeSpent: formatTime(progress.time_spent_minutes),
      score: progress.score ? `${progress.score}%` : 'No score',
      attempts: progress.attempts,
      lastAccessed: formatDate(progress.last_accessed_at),
      notes: progress.notes || ''
    };
  }

  // Group progress by module
  groupProgressByModule(progressList: LearningProgress[]): { [moduleId: string]: LearningProgress[] } {
    return progressList.reduce((grouped, progress) => {
      const moduleId = progress.module_id;
      if (!grouped[moduleId]) {
        grouped[moduleId] = [];
      }
      grouped[moduleId].push(progress);
      return grouped;
    }, {} as { [moduleId: string]: LearningProgress[] });
  }

  // Group progress by status
  groupProgressByStatus(progressList: LearningProgress[]): {
    not_started: LearningProgress[];
    in_progress: LearningProgress[];
    completed: LearningProgress[];
    paused: LearningProgress[];
  } {
    return progressList.reduce((grouped, progress) => {
      grouped[progress.status].push(progress);
      return grouped;
    }, {
      not_started: [] as LearningProgress[],
      in_progress: [] as LearningProgress[],
      completed: [] as LearningProgress[],
      paused: [] as LearningProgress[]
    });
  }

  // Sort progress by various criteria
  sortProgress(
    progressList: LearningProgress[], 
    sortBy: 'completion_percentage' | 'time_spent_minutes' | 'last_accessed_at' | 'created_at' | 'score' | 'module_title', 
    order: 'asc' | 'desc' = 'desc'
  ): LearningProgress[] {
    return [...progressList].sort((a, b) => {
      let aValue: string | number | Date;
      let bValue: string | number | Date;

      switch (sortBy) {
        case 'completion_percentage':
          aValue = a.completion_percentage;
          bValue = b.completion_percentage;
          break;
        case 'time_spent_minutes':
          aValue = a.time_spent_minutes;
          bValue = b.time_spent_minutes;
          break;
        case 'score':
          aValue = a.score || 0;
          bValue = b.score || 0;
          break;
        case 'last_accessed_at':
          aValue = new Date(a.last_accessed_at || '1970-01-01');
          bValue = new Date(b.last_accessed_at || '1970-01-01');
          break;
        case 'created_at':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case 'module_title':
          aValue = (a.module_title || '').toLowerCase();
          bValue = (b.module_title || '').toLowerCase();
          break;
        default:
          aValue = new Date(a.last_accessed_at || '1970-01-01');
          bValue = new Date(b.last_accessed_at || '1970-01-01');
      }

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }
}

export const learningProgressService = new LearningProgressService();
